import React from 'react';
import { useColorTheme } from '../../context/ColorThemeContext';

const SavedFeedbackModal = ({ isOpen, onClose, savedFeedbackData }) => {
  const { colorTheme } = useColorTheme();
  
  if (!isOpen || !savedFeedbackData) return null;

  const { feedback, statistics, consolidated_suggestions, generation_info, saved_at, campaign } = savedFeedbackData;

  const getSentimentColor = (sentiment) => {
    const positiveColors = colorTheme === 'purple' ? 'text-purple-600 bg-purple-100' : 'text-green-600 bg-green-100';
    switch (sentiment) {
      case 'positive': return positiveColors;
      case 'negative': return 'text-red-600 bg-red-100';
      default: return positiveColors;
    }
  };

  const getUserTypeIcon = (userType) => {
    switch (userType) {
      case 'enthusiast': return '😍';
      case 'skeptical': return '🤔';
      case 'expert': return '🎓';
      case 'critic': return '😐';
      default: return '👤';
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <span key={i} className={i < Math.floor(rating) ? (colorTheme === 'purple' ? 'text-purple-500' : 'text-green-500') : 'text-gray-300'}>
        ★
      </span>
    ));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
            📊 Saved AI Feedback
            <span className="ml-3 text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
              {campaign?.product?.product_name}
            </span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {/* Saved Date */}
          <div className="mb-6 bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Feedback Saved On</h3>
                <p className="text-gray-800 dark:text-gray-200">{new Date(saved_at).toLocaleString()}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Generated Using</h3>
                <p className="text-gray-800 dark:text-gray-200">{generation_info?.model_used || 'AI Model'}</p>
              </div>
            </div>
          </div>

          {/* Statistics */}
          {statistics && (
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                📊 Feedback Statistics
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">{statistics.average_rating}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Average Rating</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">{statistics.total_feedback}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Total Reviews</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600">
                    {Math.round((statistics.sentiment_distribution?.positive || 0) / statistics.total_feedback * 100) || 0}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Positive</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600">
                    {Math.round((statistics.sentiment_distribution?.negative || 0) / statistics.total_feedback * 100) || 0}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Needs Work</div>
                </div>
              </div>
              
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Rating Distribution</h4>
                <div className="flex items-center space-x-4">
                  {[5, 4, 3, 2, 1].map(rating => (
                    <div key={rating} className="flex items-center space-x-1">
                      <span className="text-sm">{rating}⭐</span>
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full"
                          style={{
                            width: `${((statistics.rating_distribution?.[rating] || 0) / statistics.total_feedback) * 100}%`
                          }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-500">
                        {statistics.rating_distribution?.[rating] || 0}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Consolidated Suggestions */}
          {consolidated_suggestions && consolidated_suggestions.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                💡 AI Improvement Recommendations
              </h3>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-l-4 border-blue-400 p-6 rounded-lg">
                <ul className="space-y-3">
                  {consolidated_suggestions.map((suggestion, idx) => (
                    <li key={idx} className="flex items-start text-blue-900 dark:text-blue-200">
                      <span className="mr-3 text-blue-600 font-bold">{idx + 1}.</span>
                      <span className="leading-relaxed">{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Individual Feedback Entries */}
          {feedback && feedback.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                💬 Individual Feedback ({feedback.length} reviews)
              </h3>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {feedback.map((item, index) => (
                  <div key={index} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{getUserTypeIcon(item.user_type)}</span>
                        <div>
                          <div className="flex items-center space-x-2">
                            <div className="flex">{renderStars(item.rating)}</div>
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              ({item.rating}/5)
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`text-xs px-2 py-1 rounded-full ${getSentimentColor(item.sentiment)}`}>
                              {item.sentiment}
                            </span>
                            <span className="text-xs text-gray-500 capitalize">
                              {item.user_type} • {item.platform}
                            </span>
                          </div>
                        </div>
                      </div>
                      <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                        AI Generated
                      </span>
                    </div>
                    
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      {item.comment}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Close Button */}
          <div className="mt-8 text-center">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SavedFeedbackModal;